import { useState } from "react";
import { motion } from "framer-motion";
import { DoorOpen, Paintbrush, Layers, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const partners = [
  { icon: DoorOpen, title: "Garage Door Repair & Install", desc: "Spring repairs, openers, full replacements." },
  { icon: Layers, title: "Epoxy Floor Coating", desc: "Showroom finish for your fresh new garage." },
  { icon: Paintbrush, title: "Interior Painting", desc: "Bright, durable paint that transforms the space." },
  { icon: Droplets, title: "Power Washing", desc: "Driveway, exterior, and garage detail wash." },
];

export const Partners = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ business: "", contact: "", email: "", message: "" });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.business.trim() || !form.email.trim()) return toast.error("Business name and email required");
    toast.success("Thanks! We'll be in touch within 48 hours.");
    setForm({ business: "", contact: "", email: "", message: "" });
    setOpen(false);
  };

  return (
    <section id="partners" className="bg-gradient-to-b from-navy to-ink py-12 md:py-16">
      <div className="container">
        <div className="mb-12 text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-gold">The Network</p>
          <h2 className="font-display text-5xl tracking-wide text-white md:text-6xl">The Full Garage Transformation. One Call.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/70">After we clean it out, we connect you with our trusted Edmonton partners — all at exclusive Garage Kings client rates.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {partners.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all hover:border-gold hover:bg-white/10"
            >
              <p.icon className="h-9 w-9 text-gold" />
              <h3 className="mt-4 font-display text-xl tracking-wide text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-white/60">{p.desc}</p>
              <span className="mt-4 inline-block rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-gold">Exclusive client discount</span>
            </motion.div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <p className="mb-5 text-white/80">We coordinate everything. You make one call.</p>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="lg" className="h-12 border-2 border-gold bg-transparent px-8 font-bold uppercase tracking-wider text-gold hover:bg-gold hover:text-ink">
                Become a Partner
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-wide">Join the Garage Kings Partner Network</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 pt-2">
                <Input placeholder="Business Name *" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} maxLength={100} required />
                <Input placeholder="Contact Name" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} maxLength={100} />
                <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={255} required />
                <Textarea placeholder="What service do you offer?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={500} />
                <Button type="submit" className="w-full bg-gold font-bold uppercase tracking-wider text-ink hover:bg-gold/90">Submit Application</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
};
